import { memo, ReactNode, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ConfigListPageShell } from '@mobileComponents/ConfigListPageShell'
import { formatReportDateTime, formatReportDeletesAt, REPORTS_TTL_DAYS_FALLBACK } from '@mobileConstants'
import { findLastLogLine } from '@mobileHelpers'
import {
  useArchiveMobileReports,
  useMobileReport,
  useRestoreMobileReports,
} from '@mobileHooks'
import { IncidentConnectivity, IncidentReport } from '@mobileTypes'
import {
  Archive as ArchiveIcon,
  ArrowBack as ArrowBackIcon,
  ContentCopy as ContentCopyIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material'

import { ROUTES } from '@constants'

const ERROR_LOG_TYPES = new Set(['crash', 'error'])
const WARNING_LOG_TYPES = new Set(['warning', 'response_error', 'push_error', 'socket_error'])

const RUNTIME_FIELD_LABELS: Record<string, string> = {
  ram: 'RAM',
  heap: 'Heap',
  disk: 'Disk',
  battery: 'Battery',
  cpu: 'CPU',
  hw: 'Hardware',
  type: 'Type',
  api: 'API',
}

const HIDDEN_RUNTIME_FIELDS = new Set(['lowRam', 'emulator', 'lowPower'])

const parseRuntimeSnapshotFields = (line: string): Array<{ label: string; value: string }> => {
  const text = line.replace(/^(crash snapshot|user report snapshot)\s*/, '').trim()
  const keys = [...Object.keys(RUNTIME_FIELD_LABELS), ...HIDDEN_RUNTIME_FIELDS].join('|')
  const re = new RegExp(`(${keys})=`, 'g')
  const matches = [...text.matchAll(re)]

  return matches.flatMap((match, index) => {
    const key = match[1]
    if (HIDDEN_RUNTIME_FIELDS.has(key)) {
      return []
    }
    const start = (match.index ?? 0) + match[0].length
    const end = index + 1 < matches.length ? (matches[index + 1].index ?? text.length) : text.length

    return [
      {
        label: RUNTIME_FIELD_LABELS[key] ?? key,
        value: text.slice(start, end).trim(),
      },
    ]
  })
}

const NETWORK_LOG_TYPES = new Set([
  'request',
  'response',
  'socket_connect',
  'socket_disconnect',
  'socket_message',
])

const isNetworkUnavailableLog = (text: string): boolean => {
  if (/потеряна/i.test(text) && !/восстановлена/i.test(text)) {
    return true
  }

  const payload = text.includes(' → ') ? text.slice(text.lastIndexOf(' → ') + 3) : text

  if (/\bdisconnected\b/.test(payload)) {
    return true
  }

  return /internet=unreachable/.test(payload) && !/m2m-unverified/.test(payload)
}

const getLogLineColor = (line: string): string => {
  const type = line.split(' ')[1]

  if (type && ERROR_LOG_TYPES.has(type)) {
    return '#ef9a9a'
  }

  if (type && WARNING_LOG_TYPES.has(type)) {
    return '#ffe082'
  }

  if (type === 'eventflow') {
    return '#ce93d8'
  }

  if (type === 'network') {
    return isNetworkUnavailableLog(line) ? '#ef9a9a' : '#80cbc4'
  }

  if (type && NETWORK_LOG_TYPES.has(type)) {
    return '#81d4fa'
  }

  return '#e0e0e0'
}

const InfoBlock = memo(({ title, children }: { title: string; children: ReactNode }) => (
  <Paper variant='outlined' sx={{ p: 2, height: '100%' }}>
    <Typography sx={{ mb: 1 }} variant='subtitle2'>
      {title}
    </Typography>
    {children}
  </Paper>
))

InfoBlock.displayName = 'InfoBlock'

const MetaLine = memo(({ label, value }: { label: string; value: string }) => (
  <Typography variant='body2'>
    <Box component='span' sx={{ color: 'text.secondary' }}>
      {label}:{' '}
    </Box>
    {value || '—'}
  </Typography>
))

MetaLine.displayName = 'MetaLine'

const ConnectivityChips = memo(({ items }: { items: IncidentConnectivity[] }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
    {items.map(item => (
      <Chip
        key={item.name}
        color={item.state === 'reachable' ? 'success' : 'error'}
        label={`${item.name}: ${item.state}`}
        size='small'
        title={item.url}
      />
    ))}
  </Box>
))

ConnectivityChips.displayName = 'ConnectivityChips'

const LogLines = memo(({ lines }: { lines: string[] }) => {
  const [search, setSearch] = useState('')

  const visibleLines = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const source = needle ? lines.filter(line => line.toLowerCase().includes(needle)) : lines

    return [...source].reverse()
  }, [lines, search])

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant='subtitle2'>Logs ({visibleLines.length})</Typography>
        <TextField
          placeholder='Filter lines'
          size='small'
          value={search}
          onChange={event => setSearch(event.target.value)}
        />
      </Box>
      <Box
        sx={{
          maxHeight: 480,
          overflow: 'auto',
          bgcolor: 'grey.900',
          color: 'grey.100',
          borderRadius: 1,
          p: 1.5,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontSize: 12,
          lineHeight: 1.5,
        }}
      >
        {visibleLines.length === 0 ? (
          <Typography color='grey.500' variant='body2'>
            No log lines
          </Typography>
        ) : (
          visibleLines.map((line, index) => (
            <Box key={`${index}-${line.slice(0, 24)}`} sx={{ color: getLogLineColor(line) }}>
              {line}
            </Box>
          ))
        )}
      </Box>
    </Box>
  )
})

LogLines.displayName = 'LogLines'

const STACK_PREVIEW_LINES = 8
const STACK_MONO_SX = {
  m: 0,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: 1.5,
} as const

const StackTraceBlock = memo(({ stack }: { stack: string }) => {
  const [expanded, setExpanded] = useState(false)
  const lines = useMemo(() => stack.replace(/\s+$/, '').split('\n'), [stack])
  const preview = lines.slice(0, STACK_PREVIEW_LINES).join('\n')
  const hasMore = lines.length > STACK_PREVIEW_LINES

  return (
    <Paper variant='outlined' sx={{ p: 2 }}>
      <Typography sx={{ mb: 1 }} variant='subtitle2'>
        Stack ({lines.length})
      </Typography>
      <Box component='pre' sx={STACK_MONO_SX}>
        {expanded || !hasMore ? stack : `${preview}\n…`}
      </Box>
      {hasMore && (
        <Button
          fullWidth
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          size='small'
          sx={{ mt: 1 }}
          onClick={() => setExpanded(current => !current)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Button>
      )}
    </Paper>
  )
})

StackTraceBlock.displayName = 'StackTraceBlock'

const ReportBody = memo(
  ({ report, headerActions }: { report: IncidentReport; headerActions: ReactNode }) => {
  const runtimeLine =
    findLastLogLine(report.lines, 'device_info', 'user report snapshot') ??
    findLastLogLine(report.lines, 'device_info', 'crash snapshot')
  const runtimeFields = runtimeLine ? parseRuntimeSnapshotFields(runtimeLine) : []

  const handleCopyLogs = () => {
    void navigator.clipboard.writeText(report.lines.join('\n'))
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ overflowWrap: 'anywhere' }} variant='h6'>
            {report.trigger.message || report.trigger.kind}
          </Typography>
          <Typography color='text.secondary' sx={{ overflowWrap: 'anywhere' }} variant='body2'>
            {report.trigger.kind} · {formatReportDateTime(report.occurredAt)} · {report.incidentId}
            {report.archivedAt
              ? ` · archived ${formatReportDateTime(report.archivedAt)} · deletes ${formatReportDeletesAt(report.archivedAt, REPORTS_TTL_DAYS_FALLBACK)}`
              : ''}
          </Typography>
        </Box>
        {headerActions}
      </Box>

      <Grid container spacing={2}>
        <Grid item md={4} xs={12}>
          <InfoBlock title='Device'>
            <MetaLine label='OS' value={`${report.device.os} ${report.device.osVersion}`} />
            <MetaLine label='Brand' value={report.device.brand} />
            <MetaLine label='Model' value={report.device.model} />
            {runtimeFields.map(field => (
              <MetaLine key={field.label} label={field.label} value={field.value} />
            ))}
          </InfoBlock>
        </Grid>
        <Grid item md={4} xs={12}>
          <InfoBlock title='App'>
            {report.app ? (
              <>
                <MetaLine label='App version' value={report.app.version} />
                <MetaLine label='Config ID' value={report.app.configId} />
                <MetaLine label='Config version' value={report.app.configVersion} />
              </>
            ) : (
              <Typography color='text.secondary' variant='body2'>
                No app snapshot
              </Typography>
            )}
          </InfoBlock>
        </Grid>
        <Grid item md={4} xs={12}>
          <InfoBlock title='Session'>
            <MetaLine label='Stand' value={report.session.stand} />
            <MetaLine label='User hash' value={report.session.userIdHash} />
            <MetaLine label='Authenticated' value={report.session.authenticated ? 'yes' : 'no'} />
            <Box sx={{ mt: 1 }}>
              <ConnectivityChips items={report.connectivity} />
            </Box>
          </InfoBlock>
        </Grid>
      </Grid>

      {report.trigger.stack && <StackTraceBlock stack={report.trigger.stack} />}

      <Divider />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button startIcon={<ContentCopyIcon />} variant='outlined' onClick={handleCopyLogs}>
          Copy logs
        </Button>
      </Box>
      <LogLines lines={report.lines} />
    </Box>
  )
})

ReportBody.displayName = 'ReportBody'

export const MobileReportDetails = () => {
  const { incidentId } = useParams<{ incidentId: string }>()
  const [searchParams] = useSearchParams()
  const archived = searchParams.get('archived') === 'true'
  const navigate = useNavigate()
  const { report, loading, isError } = useMobileReport(incidentId, archived)
  const [archiveReports, { isLoading: archiving }] = useArchiveMobileReports()
  const [restoreReports, { isLoading: restoring }] = useRestoreMobileReports()

  const handleMove = async () => {
    if (!incidentId) {
      return
    }
    if (archived) {
      await restoreReports({ incidentIds: [incidentId] }).unwrap()
    } else {
      await archiveReports({ incidentIds: [incidentId] }).unwrap()
    }
    navigate(ROUTES.CONFIG_MOBILE_REPORTS)
  }

  const headerActions = (
    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
      <Button
        disabled={!incidentId || archiving || restoring}
        startIcon={archived ? <UnarchiveIcon /> : <ArchiveIcon />}
        variant='contained'
        onClick={() => void handleMove()}
      >
        {archived ? 'Restore' : 'Archive'}
      </Button>
      <Button
        startIcon={<ArrowBackIcon />}
        variant='outlined'
        onClick={() => navigate(ROUTES.CONFIG_MOBILE_REPORTS)}
      >
        Back
      </Button>
    </Box>
  )

  return (
    <ConfigListPageShell title='Mobile Report'>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {isError && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>{headerActions}</Box>
          <Alert severity='error'>Report not found or reports API is unavailable.</Alert>
        </>
      )}
      {!loading && !isError && report && <ReportBody headerActions={headerActions} report={report} />}
    </ConfigListPageShell>
  )
}
